import React, { useState, useEffect } from 'react';
import {
  Flex,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  Spinner,
  useDisclosure,
  useToast,
  Tooltip,
  Icon,
} from '@chakra-ui/react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { BsFillInfoCircleFill } from 'react-icons/all';
import { Button, Footer, Heading, Input, Select, Textarea } from '~/components';
import { isActive } from '~/utils/isActive';
import {
  useCreateEquipment,
  useEquipmentById,
  useUpdateEquipment,
} from '~/hooks/useEquipments';
import { EquipmentForm as EquipmentFormType, EquipmentType } from '~/types';
import { useEquipmentModelsAsSelect, useEquipmentTypesAsSelect } from '~/hooks';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import {
  getEquipmentDescriptionByType,
  getEquipmentTypeByModelId,
  getEquipmentTypePreview,
} from '~/services';

export interface Item {
  label: string;
  value: string;
}
const updateFormSchema = yup.object().shape({
  cd_equipamento: yup.string().required('Código obrigatório'),
  id_modeloequipamento: yup.string().required('Modelo obrigatório'),
});

export const EquipmentForm: React.VFC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const isEdition = !!id;
  const { data, isLoading: isEquipmentLoading } = useEquipmentById(id);
  const updateEquipment = useUpdateEquipment();
  const createEquipment = useCreateEquipment();
  const [status, setStatus] = useState<boolean>(true);
  const { data: equipmentModels, isLoading: isEquipmentModelsLoading } =
    useEquipmentModelsAsSelect();
  const { register, handleSubmit, setValue, formState, setError, clearErrors } =
    useForm({
      resolver: yupResolver(updateFormSchema),
    });
  const { errors } = formState;
  const isLoading = isEquipmentLoading || isEquipmentModelsLoading;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [modelo_equipamento, setModeloEquipamento] = useState<number>(
    Number(data?.modelo_equipamento?.id_modeloequipamento),
  );
  const [tipo_equipamento, setTipo_equipamento] = useState<EquipmentType>();

  useEffect(() => {
    if (data) {
      setValue('cd_equipamento', data?.cd_equipamento);
      setValue('ds_equipamento', data?.ds_equipamento);
      setValue(
        'id_modeloequipamento',
        Number(data?.modelo_equipamento?.id_modeloequipamento),
      );
      setValue('tp_equipamento', data?.tipo_equipamento?.nm_tipoequipamento);
      setModeloEquipamento(
        Number(data?.modelo_equipamento?.id_modeloequipamento),
      );
      setStatus(data.status);
    }
  }, [data]);

  async function handleSave(formData: EquipmentFormType) {
    if (isEdition) {
      await updateEquipment.mutateAsync({
        id: String(data?.id_equipamento),
        formData: {
          ...formData,
          tp_situacao: status ? '1' : '0',
          id_modeloequipamento: modelo_equipamento,
          ds_equipamento: formData.ds_equipamento.trim(),
        },
      });
    } else {
      await createEquipment.mutateAsync({
        formData: {
          ...formData,
          tp_situacao: status ? '1' : '0',
          id_modeloequipamento: modelo_equipamento,
          ds_equipamento: formData.ds_equipamento.trim(),
        },
      });
    }
    history.goBack();
  }

  if (isLoading) return <Spinner />;

  const resetarDescricao = () => {
    onClose();
    getEquipmentTypePreview(Number(id))
      .then(res => {
        setValue('ds_equipamento', res);
      })
      .catch(() => {
        toast({
          title: 'Erro',
          description: 'Não foi possível restaurar a descrição do equipamento.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const mudarTipoEquipamento = (id_modelo: string) => {
    getEquipmentTypeByModelId(Number(id_modelo)).then(res => {
      res && setValue('tp_equipamento', res.nm_tipoequipamento);
      res && setTipo_equipamento(res);
      res && mudarDescricao(res.id_tipoequipamento);
    });
  };

  const mudarDescricao = (id_tipoequipamento: number) => {
    console.log(id_tipoequipamento);
    getEquipmentDescriptionByType(Number(id), id_tipoequipamento)
      .then(res => {
        setValue('ds_equipamento', res);
      })
      .catch(() => {
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar descrição do equipamento.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const changeModelo = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value) {
      mudarTipoEquipamento(e.target.value);
      clearErrors('id_modeloequipamento');
    } else {
      setError('id_modeloequipamento', {
        type: 'required',
        message: `Modelo obrigatório`,
      });
    }
    setModeloEquipamento(Number(e.target.value));
    setValue('id_modeloequipamento', e.target.value);
  };

  return (
    <>
      <Flex
        flexDir="column"
        justify="space-between"
        h="100%"
        as="form"
        onSubmit={handleSubmit(handleSave)}
      >
        <Flex flexDir="column" px="8" py="4">
          <Heading hasBackButton>
            {isEdition ? 'Editar Equipamento' : 'Cadastrar Equipamento'}
          </Heading>
          <Flex
            flexDir={{ md: 'row', sm: 'column' }}
            w="100%"
            py={9}
            gridGap="12"
          >
            <Stack gridGap={6} bg="white" maxW={300} w="100%">
              <Stack gridGap={4}>
                <Heading fontSize="md">Dados Gerais</Heading>
                <Input
                  isDisabled={isEdition}
                  name="cd_equipamento"
                  type="text"
                  label="SKU"
                  register={register}
                  error={errors?.cd_equipamento}
                  fontSize="15px"
                />
                <Input
                  name="tp_equipamento"
                  placeholder="Selecione"
                  label="Tipo de equipamento"
                  register={register}
                  error={errors.tp_equipamento}
                  fontSize="15px"
                  isDisabled
                />
                <Select
                  name="id_modeloequipamento"
                  placeholder="Selecione"
                  label="Modelo"
                  value={modelo_equipamento.toString()}
                  error={errors.id_modeloequipamento}
                  fontSize="15px"
                  onChange={e => {
                    changeModelo(e);
                  }}
                >
                  {equipmentModels?.map(model => (
                    <option key={model.value} value={Number(model.value)}>
                      {model.label}
                    </option>
                  ))}
                </Select>
                <FormControl>
                  <FormLabel
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    <span>
                      Descrição{' '}
                      {data?.descricao_alterada_manualmente && (
                        <Tooltip
                          hasArrow
                          label="Essa descrição foi alterada."
                          bg="gray"
                          shouldWrapChildren
                        >
                          <Icon
                            color="black"
                            marginLeft="auto"
                            as={BsFillInfoCircleFill}
                          />
                        </Tooltip>
                      )}
                    </span>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <span
                      style={{
                        color: '#0082B8',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                      onClick={onOpen}
                    >
                      Resetar descrição
                    </span>
                  </FormLabel>
                  <Textarea
                    name="ds_equipamento"
                    resize="none"
                    register={register}
                    error={errors?.ds_equipamento}
                    fontSize="15px"
                  />
                </FormControl>
              </Stack>
              <Stack gridGap={4}>
                <Heading fontSize="md">Situação</Heading>
                <FormControl
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <FormLabel htmlFor="status" mb="0">
                    {isActive(status)}
                  </FormLabel>
                  <Switch
                    id="status"
                    size="lg"
                    colorScheme="green"
                    {...register('tp_situacao')}
                    isChecked={status}
                    onChange={e => setStatus(e.target.checked)}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </Flex>
        </Flex>
        <Footer>
          <Button variant="secondary" onClick={() => history.goBack()}>
            Cancelar
          </Button>
          <Button type="submit">Alterar</Button>
        </Footer>
      </Flex>
      <ConfirmationDialog
        confirmar={resetarDescricao}
        onClose={onClose}
        isOpen={isOpen}
        titulo="Resetar Descrição"
      >
        <span>
          Ao continuar a descrição do equipamento voltará a ser igual a do SAP.
          Tem certeza que deseja continuar?
        </span>
      </ConfirmationDialog>
    </>
  );
};
